import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api, { eur, formatApiError } from "@/lib/api";
import { PageHeader, Btn, Modal, Field, inputCls } from "@/components/common";
import StaffTips from "@/components/StaffTips";
import { toEur, CURRENCIES, SYMBOL } from "@/lib/expenseConstants";
import { Plus, Pencil, Ban, CheckCircle2, Trash2, GripVertical, Copy } from "lucide-react";

const MONTHS_IT = ["Gen", "Feb", "Mar", "Apr", "Mag", "Giu", "Lug", "Ago", "Set", "Ott", "Nov", "Dic"];
const SC_MONTHS = [11, 2, 5, 8]; // Service Charge ogni 3 mesi ancorata a Novembre
const RAMADAN_MONTH = { 2024: 3, 2025: 3, 2026: 2, 2027: 2, 2028: 1, 2029: 1, 2030: 1 }; // mese iniziale Ramadan
const SC_LABEL = "Service Charge";
const RAM_LABEL = "Ramadan Bonus";
const thisYear = () => new Date().getFullYear();
const thisMonthStr = () => new Date().toISOString().slice(0, 7);
const todayStr = () => new Date().toISOString().slice(0, 10);
const fmtDate = (d) => { if (!d) return ""; const [y, m, dd] = d.slice(0, 10).split("-"); return `${dd}/${m}/${y}`; };

const emptyStaff = { name: "", role: "", currency: "EUR", start_date: "", conto_corrente: "", contract_end: "" };

export default function Staff() {
  const [items, setItems] = useState([]);
  const [year, setYear] = useState(String(thisYear()));
  const [rates, setRates] = useState({ EUR: 1, USD: null, MVR: null });
  const [showInactive, setShowInactive] = useState(false);
  const [anagOpen, setAnagOpen] = useState(false);
  const [anagForm, setAnagForm] = useState(emptyStaff);
  const [anagId, setAnagId] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [dragId, setDragId] = useState(null);

  const load = () => api.get("/staff").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  useEffect(() => { api.get("/fx/rates").then((r) => setRates(r.data.rates)); }, []);

  const ramMonth = RAMADAN_MONTH[Number(year)] || null;
  const months = useMemo(() => MONTHS_IT.map((_, i) => ({ idx: i + 1, ym: `${year}-${String(i + 1).padStart(2, "0")}`, name: MONTHS_IT[i] })), [year]);
  const isActive = (m) => !m.deactivated_at;
  const visibleRows = items.filter((m) => showInactive || isActive(m));

  const entryOf = (m, ym) => (m.months || {})[ym] || {};
  const monthExcluded = (m, ym) => {
    const ce = m.contract_end || "";
    if (ce && ym > ce) return true;
    return false;
  };
  const bonusAmount = (m, ym, label) => { const b = (entryOf(m, ym).bonuses || []).find((x) => x.label === label); return b ? b.amount : ""; };

  const draftKey = (id, ym, field) => `${id}|${ym}|${field}`;
  const cellVal = (m, ym, field) => {
    const k = draftKey(m.id, ym, field);
    if (drafts[k] !== undefined) return drafts[k];
    const raw = field === "salary" ? entryOf(m, ym).salary : bonusAmount(m, ym, field === "sc" ? SC_LABEL : RAM_LABEL);
    return raw ? String(raw) : "";
  };
  const setDraft = (m, ym, field, v) => setDrafts((d) => ({ ...d, [draftKey(m.id, ym, field)]: v }));

  const buildUpdated = (m, ym, field, value) => {
    const months2 = { ...(m.months || {}) };
    const entry = { salary: 0, bonuses: [], ...(months2[ym] || {}) };
    entry.bonuses = [...(entry.bonuses || [])];
    if (field === "salary") {
      entry.salary = value;
    } else {
      const label = field === "sc" ? SC_LABEL : RAM_LABEL;
      const idx = entry.bonuses.findIndex((b) => b.label === label);
      if (value > 0) {
        if (idx >= 0) entry.bonuses[idx] = { label, amount: value };
        else entry.bonuses.push({ label, amount: value });
      } else if (idx >= 0) entry.bonuses.splice(idx, 1);
    }
    months2[ym] = entry;
    return { ...m, months: months2 };
  };

  const saveCell = async (m, ym, field) => {
    const k = draftKey(m.id, ym, field);
    if (drafts[k] === undefined) return;
    const value = Number(drafts[k]) || 0;
    const prev = field === "salary" ? Number(entryOf(m, ym).salary || 0) : Number(bonusAmount(m, ym, field === "sc" ? SC_LABEL : RAM_LABEL) || 0);
    if (value === prev) { setDrafts((d) => { const n = { ...d }; delete n[k]; return n; }); return; }
    try {
      const updated = buildUpdated(m, ym, field, value);
      await api.put(`/staff/${m.id}`, updated);
      setDrafts((d) => { const n = { ...d }; delete n[k]; return n; });
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const monthEur = (m, ym) => {
    if (monthExcluded(m, ym)) return 0;
    const e = entryOf(m, ym);
    const raw = Number(e.salary || 0) + (e.bonuses || []).reduce((a, b) => a + Number(b.amount || 0), 0);
    return toEur(raw, m.currency, rates);
  };
  const rowTotalEur = (m) => months.reduce((a, mm) => a + monthEur(m, mm.ym), 0);
  const colTotalEur = (ym) => visibleRows.reduce((a, m) => a + monthEur(m, ym), 0);
  const grandTotal = visibleRows.reduce((a, m) => a + rowTotalEur(m), 0);

  const bonusEur = (m, ym, label) => {
    if (monthExcluded(m, ym) || !isActive(m)) return 0;
    return toEur(Number(bonusAmount(m, ym, label) || 0), m.currency, rates);
  };
  const scCol = (ym) => visibleRows.reduce((a, m) => a + bonusEur(m, ym, SC_LABEL), 0);
  const ramCol = (ym) => visibleRows.reduce((a, m) => a + bonusEur(m, ym, RAM_LABEL), 0);
  const scAnnual = months.reduce((a, mm) => a + scCol(mm.ym), 0);
  const ramAnnual = months.reduce((a, mm) => a + ramCol(mm.ym), 0);

  const openNew = () => { setAnagForm(emptyStaff); setAnagId(null); setAnagOpen(true); };
  const openAnag = (m) => { setAnagForm({ name: m.name, role: m.role || "", currency: m.currency || "EUR", start_date: m.start_date || "", conto_corrente: m.conto_corrente || "", contract_end: m.contract_end || "" }); setAnagId(m.id); setAnagOpen(true); };
  const saveAnag = async (e) => {
    e.preventDefault();
    if (!anagForm.name.trim()) { toast.error("Nome richiesto"); return; }
    try {
      if (anagId) {
        const m = items.find((x) => x.id === anagId);
        await api.put(`/staff/${anagId}`, { ...m, name: anagForm.name, role: anagForm.role, currency: anagForm.currency, start_date: anagForm.start_date, conto_corrente: anagForm.conto_corrente, contract_end: anagForm.contract_end });
      } else {
        await api.post("/staff", { ...anagForm, months: {} });
      }
      toast.success("Dipendente salvato"); setAnagOpen(false); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const remove = async (id) => { if (!window.confirm("Eliminare il dipendente?")) return; await api.delete(`/staff/${id}`); load(); };

  const toggleActive = async (m) => {
    const willActivate = !isActive(m);
    try {
      await api.put(`/staff/${m.id}`, {
        ...m,
        deactivated_at: willActivate ? "" : todayStr(),
        contract_end: willActivate ? "" : thisMonthStr(),
      });
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const onDrop = async (targetId) => {
    if (!dragId || dragId === targetId) { setDragId(null); return; }
    const cur = [...items];
    const from = cur.findIndex((r) => r.id === dragId);
    const to = cur.findIndex((r) => r.id === targetId);
    if (from < 0 || to < 0) { setDragId(null); return; }
    const [moved] = cur.splice(from, 1);
    cur.splice(to, 0, moved);
    setItems(cur); setDragId(null);
    try { await api.put("/staff/reorder", { ids: cur.map((r) => r.id) }); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); load(); }
  };

  const years = [thisYear() - 1, thisYear(), thisYear() + 1, thisYear() + 2].map(String);
  const copyPrevMonth = async (i) => {
    if (i < 1) return;
    const targetYm = months[i].ym; const prevYm = months[i - 1].ym;
    if (!window.confirm(`Copiare gli stipendi di ${MONTHS_IT[i - 1]} in ${MONTHS_IT[i]} ${year}? Gli stipendi esistenti verranno sovrascritti (i bonus Service Charge/Ramadan non vengono copiati). Potrai comunque modificarli dopo.`)) return;
    try {
      for (const m of visibleRows.filter((x) => isActive(x))) {
        if (monthExcluded(m, targetYm)) continue;
        const prevSalary = Number(entryOf(m, prevYm).salary || 0);
        if (prevSalary > 0) await api.put(`/staff/${m.id}`, buildUpdated(m, targetYm, "salary", prevSalary));
      }
      toast.success(`Copiato ${MONTHS_IT[i - 1]} → ${MONTHS_IT[i]}`);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const cellInput = (m, ym, field, disabled, ph) => (
    <input data-testid={`staff-cell-${field}-${m.id}-${ym}`} value={cellVal(m, ym, field)} disabled={disabled}
      onChange={(e) => setDraft(m, ym, field, e.target.value.replace(/[^0-9.]/g, ""))}
      onBlur={() => saveCell(m, ym, field)} onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
      className="w-[70px] text-right tabular rounded border border-transparent hover:border-slate-200 focus:border-sky-500 focus:outline-none px-1.5 py-1 disabled:bg-transparent" placeholder={ph} />
  );

  return (
    <div>
      <PageHeader title="Staff" subtitle="Griglia dipendenti × mesi. Stipendio per cella; colonne automatiche Service Charge (Nov/Feb/Mag/Ago) e Ramadan; trascina per riordinare.">
        <select data-testid="staff-year" value={year} onChange={(e) => setYear(e.target.value)} className={inputCls + " w-auto"}>
          {years.map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
        <label className="flex items-center gap-1.5 text-sm text-slate-600 px-2">
          <input type="checkbox" data-testid="staff-show-inactive" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />
          Mostra disattivati
        </label>
        <Btn data-testid="add-staff-btn" onClick={openNew}><Plus size={16} /> Nuovo dipendente</Btn>
      </PageHeader>

      <div className="mb-4">
        <span className="text-sm text-slate-500">Totale staff {year} (€): </span>
        <span data-testid="staff-grand-total" className="text-lg font-semibold text-red-600">{eur(grandTotal)}</span>
        <span className="text-xs text-slate-400 ml-3">Ramadan {year}: {ramMonth ? MONTHS_IT[ramMonth - 1] : "n/d"}</span>
      </div>

      <div className="bg-white border border-slate-200 rounded-md overflow-auto max-h-[70vh]">
        <table className="text-sm border-collapse">
          <thead>
            <tr className="text-xs uppercase text-slate-500 border-b border-slate-200 bg-slate-50">
              <th className="px-3 py-2.5 text-left sticky left-0 top-0 bg-slate-50 z-30 min-w-[220px]">Dipendente</th>
              {months.map((mm, i) => (
                <th key={mm.ym} className="px-2 py-2.5 text-right min-w-[80px] sticky top-0 bg-slate-50 z-20">
                  <div className="flex items-center justify-end gap-1">
                    {i >= 1 && <button data-testid={`staff-copy-${i}`} onClick={() => copyPrevMonth(i)} title={`Copia stipendi ${MONTHS_IT[i - 1]} in ${mm.name}`} className="p-2 -m-2 md:p-0 md:m-0 text-slate-300 hover:text-sky-600"><Copy size={12} /></button>}
                    {mm.name}
                  </div>
                  {SC_MONTHS.includes(mm.idx) && <span className="block text-[9px] text-emerald-600 normal-case">+ Serv.Charge</span>}
                  {ramMonth === mm.idx && <span className="block text-[9px] text-amber-600 normal-case">+ Ramadan</span>}
                </th>
              ))}
              <th className="px-3 py-2.5 text-right min-w-[90px] sticky top-0 bg-slate-50 z-20">Totale €</th>
              <th className="px-2 py-2.5 sticky top-0 bg-slate-50 z-20"></th>
            </tr>
          </thead>
          <tbody data-testid="staff-grid-table">
            {visibleRows.map((m) => (
              <tr key={m.id} data-testid={`staff-row-${m.id}`}
                  onDragOver={(e) => e.preventDefault()} onDrop={() => onDrop(m.id)}
                  className={`border-b border-slate-100 hover:bg-slate-50 ${!isActive(m) ? "opacity-50" : ""} ${dragId === m.id ? "bg-sky-50" : ""}`}>
                <td className="px-2 py-2 sticky left-0 bg-white z-10">
                  <div className="flex items-start gap-1.5">
                    <span draggable data-testid={`staff-drag-${m.id}`} onDragStart={() => setDragId(m.id)} onDragEnd={() => setDragId(null)}
                          className="cursor-grab text-slate-300 hover:text-slate-500 pt-0.5" title="Trascina per riordinare"><GripVertical size={15} /></span>
                    <div>
                      <div className="font-medium text-slate-800 flex items-center gap-2">
                        {m.name}
                        {!isActive(m) && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-200 text-slate-600">disattivato{m.deactivated_at ? ` il ${fmtDate(m.deactivated_at)}` : ""}</span>}
                      </div>
                      <div className="text-xs text-slate-400">
                        {m.role ? m.role + " · " : ""}{SYMBOL[m.currency] || m.currency}
                        {m.start_date && <> · assunto {fmtDate(m.start_date)}</>}
                      </div>
                      {m.conto_corrente && <div className="text-xs text-slate-400">C/C: {m.conto_corrente}</div>}
                    </div>
                  </div>
                </td>
                {months.map((mm) => {
                  const blocked = monthExcluded(m, mm.ym) || !isActive(m);
                  const hasSal = Number(entryOf(m, mm.ym).salary || 0) > 0;
                  const hasSc = Number(bonusAmount(m, mm.ym, SC_LABEL) || 0) > 0;
                  const hasRam = Number(bonusAmount(m, mm.ym, RAM_LABEL) || 0) > 0;
                  return (
                    <td key={mm.ym} className="px-1 py-1 text-right align-top">
                      {cellInput(m, mm.ym, "salary", blocked && !hasSal, "—")}
                      {SC_MONTHS.includes(mm.idx) && (
                        <div className="mt-0.5 flex items-center justify-end gap-0.5">
                          <span className="text-[9px] text-emerald-600">SC</span>{cellInput(m, mm.ym, "sc", blocked && !hasSc, "0")}
                        </div>
                      )}
                      {ramMonth === mm.idx && (
                        <div className="mt-0.5 flex items-center justify-end gap-0.5">
                          <span className="text-[9px] text-amber-600">Ram</span>{cellInput(m, mm.ym, "ram", blocked && !hasRam, "0")}
                        </div>
                      )}
                    </td>
                  );
                })}
                <td className="px-3 py-2 text-right tabular font-medium text-slate-700 align-top">{eur(rowTotalEur(m))}</td>
                <td className="px-2 py-2 align-top">
                  <div className="flex gap-1 justify-end">
                    <button data-testid={`edit-staff-${m.id}`} onClick={() => openAnag(m)} className="p-1.5 text-slate-400 hover:text-sky-600" title="Anagrafica"><Pencil size={15} /></button>
                    <button data-testid={`staff-toggle-${m.id}`} onClick={() => toggleActive(m)} className="p-1.5 text-slate-400 hover:text-amber-600" title={isActive(m) ? "Disattiva" : "Riattiva"}>
                      {isActive(m) ? <Ban size={15} /> : <CheckCircle2 size={15} />}
                    </button>
                    <button data-testid={`del-staff-${m.id}`} onClick={() => remove(m.id)} className="p-1.5 text-slate-400 hover:text-red-600" title="Elimina"><Trash2 size={15} /></button>
                  </div>
                </td>
              </tr>
            ))}
            {visibleRows.length === 0 && (
              <tr><td colSpan={16} className="px-3 py-12 text-center text-slate-400">Nessun dipendente.</td></tr>
            )}
          </tbody>
          {visibleRows.length > 0 && (
            <tfoot>
              <tr className="border-t border-slate-200 bg-slate-50 font-medium text-slate-700">
                <td className="px-3 py-2.5 sticky left-0 bg-slate-50 z-10">Totale mensile (€)</td>
                {months.map((mm) => <td key={mm.ym} className="px-2 py-2.5 text-right tabular text-xs">{colTotalEur(mm.ym) ? eur(colTotalEur(mm.ym)) : "—"}</td>)}
                <td className="px-3 py-2.5 text-right tabular text-red-600">{eur(grandTotal)}</td>
                <td></td>
              </tr>
              <tr className="bg-white text-xs text-emerald-700" data-testid="staff-sc-row">
                <td className="px-3 py-2 sticky left-0 bg-white z-10">di cui Service Charge (€)</td>
                {months.map((mm) => <td key={mm.ym} className="px-2 py-2 text-right tabular">{scCol(mm.ym) ? eur(scCol(mm.ym)) : "—"}</td>)}
                <td className="px-3 py-2 text-right tabular font-medium" data-testid="staff-sc-annual">{eur(scAnnual)}</td>
                <td></td>
              </tr>
              <tr className="bg-white text-xs text-amber-700" data-testid="staff-ram-row">
                <td className="px-3 py-2 sticky left-0 bg-white z-10">di cui Ramadan (€)</td>
                {months.map((mm) => <td key={mm.ym} className="px-2 py-2 text-right tabular">{ramCol(mm.ym) ? eur(ramCol(mm.ym)) : "—"}</td>)}
                <td className="px-3 py-2 text-right tabular font-medium" data-testid="staff-ram-annual">{eur(ramAnnual)}</td>
                <td></td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
      <p className="text-xs text-slate-400 mt-3">
        Gli importi sono nella valuta del dipendente; i Totali sono convertiti in € al cambio attuale (1 € = {rates.USD ?? "…"} $ = {rates.MVR ?? "…"} Rf).
        La data di assunzione è solo informativa: puoi inserire i costi di qualsiasi mese (anche precedenti). Disattivando un dipendente si registra la data e imposta la chiusura al mese corrente, così i mesi successivi non contano; i mesi già inseriti restano nei report.
      </p>

      <StaffTips staff={items} year={year} />
      <Modal open={anagOpen} onClose={() => setAnagOpen(false)} title={anagId ? "Anagrafica dipendente" : "Nuovo dipendente"}>
        <form onSubmit={saveAnag} className="space-y-4">
          <Field label="Nome"><input required value={anagForm.name} onChange={(e) => setAnagForm({ ...anagForm, name: e.target.value })} className={inputCls} data-testid="st-name" /></Field>
          <Field label="Ruolo"><input value={anagForm.role} onChange={(e) => setAnagForm({ ...anagForm, role: e.target.value })} className={inputCls} data-testid="st-role" /></Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Valuta">
              <select value={anagForm.currency} onChange={(e) => setAnagForm({ ...anagForm, currency: e.target.value })} className={inputCls} data-testid="st-currency">
                {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
            <Field label="Data assunzione (opz.)"><input type="date" value={anagForm.start_date ? anagForm.start_date.slice(0, 10) : ""} onChange={(e) => setAnagForm({ ...anagForm, start_date: e.target.value })} className={inputCls} data-testid="st-start" /></Field>
          </div>
          <Field label="Conto corrente bancario"><input value={anagForm.conto_corrente} onChange={(e) => setAnagForm({ ...anagForm, conto_corrente: e.target.value })} className={inputCls} data-testid="st-conto" placeholder="Es. IBAN o nome conto" /></Field>
          <Field label="Mese chiusura contratto (opz.) — dopo questo mese non conta più">
            <input type="month" value={anagForm.contract_end} onChange={(e) => setAnagForm({ ...anagForm, contract_end: e.target.value })} className={inputCls} data-testid="st-contract-end" />
          </Field>
          <div className="flex justify-end gap-2 pt-2">
            <Btn type="button" variant="secondary" onClick={() => setAnagOpen(false)}>Annulla</Btn>
            <Btn type="submit" data-testid="save-staff">Salva</Btn>
          </div>
        </form>
      </Modal>
    </div>
  );
}
